/**
 * Visitor & session identity + journey tracking.
 * - visitor_id: UUID persisted in localStorage (2yr). Same visitor across sessions.
 * - session_id: UUID persisted in sessionStorage + last-activity in localStorage.
 *   Rolls over after 30 minutes of inactivity.
 * - Captures first-touch attribution (referrer + UTM) once and preserves it.
 * - Tracks visit_count and has_booked flags for the returning-visitor modal.
 */

const LS = {
  visitorId: "gr_visitor_id",
  visitorCreatedAt: "gr_visitor_created_at",
  visitCount: "gr_visit_count",
  lastVisitAt: "gr_last_visit_at",
  lastActivity: "gr_last_activity",
  firstReferrer: "gr_first_referrer",
  firstUtm: "gr_first_utm",
  hasBooked: "gr_has_booked",
  modalDismissedAt: "gr_discount_modal_dismissed_at",
  modalDismissCount: "gr_discount_modal_dismiss_count",
  modalSkipUntilVisit: "gr_discount_modal_skip_until_visit",
  sourceNeighborhood: "gr_source_neighborhood",
  sourceLocationSlug: "gr_source_location_slug",
  sourcePage: "gr_source_page",
};
const SS = {
  sessionId: "gr_session_id",
  sessionStartedAt: "gr_session_started_at",
  visitCounted: "gr_session_visit_counted",
};

const SESSION_IDLE_MS = 30 * 60 * 1000; // 30 min
const VISITOR_TTL_MS = 2 * 365 * 24 * 60 * 60 * 1000; // ~2 yrs

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function safeLS(): Storage | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}
function safeSS(): Storage | null {
  try { return typeof window !== "undefined" ? window.sessionStorage : null; } catch { return null; }
}

export function getVisitorId(): string {
  const ls = safeLS();
  if (!ls) return "anon";
  let id = ls.getItem(LS.visitorId);
  const createdAt = Number(ls.getItem(LS.visitorCreatedAt) || 0);
  const expired = createdAt && Date.now() - createdAt > VISITOR_TTL_MS;
  if (!id || expired) {
    id = uuid();
    ls.setItem(LS.visitorId, id);
    ls.setItem(LS.visitorCreatedAt, String(Date.now()));
    ls.setItem(LS.visitCount, "0");
  }
  return id;
}

export function getSessionId(): string {
  const ss = safeSS();
  const ls = safeLS();
  if (!ss) return "anon-session";
  const now = Date.now();
  const lastActivity = Number(ls?.getItem(LS.lastActivity) || 0);
  const stale = lastActivity && now - lastActivity > SESSION_IDLE_MS;
  let sid = ss.getItem(SS.sessionId);
  if (!sid || stale) {
    sid = uuid();
    ss.setItem(SS.sessionId, sid);
    ss.setItem(SS.sessionStartedAt, String(now));
  }
  if (ls) ls.setItem(LS.lastActivity, String(now));
  return sid;
}

export function isNewSession(): boolean {
  const ss = safeSS();
  if (!ss) return false;
  const startedAt = Number(ss.getItem(SS.sessionStartedAt) || 0);
  return !!startedAt && Date.now() - startedAt < 2000; // within 2s of start
}

export function getVisitCount(): number {
  const ls = safeLS();
  return Number(ls?.getItem(LS.visitCount) || 0);
}

export function incrementVisitIfNewSession(): void {
  const ls = safeLS();
  const ss = safeSS();
  if (!ls || !ss) return;
  // Only bump the visit counter once per session. Without this guard, any
  // route change within the first 2s of a session (a very common first-touch
  // pattern) increments the counter again and triggers the "Welcome back"
  // modal on a brand-new visitor's first visit.
  if (ss.getItem(SS.visitCounted) === "1") return;
  if (isNewSession()) {
    const c = getVisitCount() + 1;
    ls.setItem(LS.visitCount, String(c));
    ls.setItem(LS.lastVisitAt, String(Date.now()));
    ss.setItem(SS.visitCounted, "1");
  }
}

export function getFirstTouch(): { referrer?: string; utm?: Record<string, string> } {
  const ls = safeLS();
  if (!ls) return {};
  let referrer = ls.getItem(LS.firstReferrer) || undefined;
  let utmRaw = ls.getItem(LS.firstUtm);
  // Capture first touch if not yet set
  if (!referrer) {
    const ref = typeof document !== "undefined" ? document.referrer : "";
    referrer = ref || "direct";
    ls.setItem(LS.firstReferrer, referrer);
  }
  if (!utmRaw) {
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const utm: Record<string, string> = {};
    if (params) {
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
        const v = params.get(k);
        if (v) utm[k] = v;
      });
    }
    if (Object.keys(utm).length > 0) {
      ls.setItem(LS.firstUtm, JSON.stringify(utm));
      utmRaw = JSON.stringify(utm);
    }
  }
  return { referrer, utm: utmRaw ? JSON.parse(utmRaw) : undefined };
}

export function getCurrentUtm(): Record<string, string | undefined> {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string | undefined> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  // Fall back to first-touch UTM if none in current URL
  if (Object.keys(utm).length === 0) {
    const ft = getFirstTouch();
    if (ft.utm) return ft.utm;
  }
  return utm;
}

export function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

/**
 * Capture source neighborhood attribution the first time we see it.
 * Priority: ?neighborhood= param > /locations/<slug> path.
 * Persisted for 90 days (sticks even after navigating to /booking).
 */
import { neighborhoodFromPath, neighborhoodNameFromSlug } from "./neighborhoods";

export function captureSourceNeighborhood(pathname: string, search: string): void {
  const ls = safeLS();
  if (!ls) return;
  const already = ls.getItem(LS.sourceNeighborhood);
  const params = new URLSearchParams(search || "");
  const qSlug = (params.get("neighborhood") || "").toLowerCase() || null;
  const qName = neighborhoodNameFromSlug(qSlug);
  const p = neighborhoodFromPath(pathname);
  const slug = qSlug && qName ? qSlug : p.slug;
  const name = qName || p.name;
  if (!slug || !name) return;
  // If already captured, only overwrite if we now see an explicit stronger signal
  // (query param on booking page), otherwise leave first-touch intact.
  if (already && !(qSlug && qName)) return;
  ls.setItem(LS.sourceNeighborhood, name);
  ls.setItem(LS.sourceLocationSlug, slug);
  ls.setItem(LS.sourcePage, pathname + (search || ""));
}

export function getSourceAttribution(): {
  source_neighborhood: string | null;
  source_location_slug: string | null;
  booking_source_page: string | null;
} {
  const ls = safeLS();
  if (!ls) return { source_neighborhood: null, source_location_slug: null, booking_source_page: null };
  return {
    source_neighborhood: ls.getItem(LS.sourceNeighborhood),
    source_location_slug: ls.getItem(LS.sourceLocationSlug),
    booking_source_page: ls.getItem(LS.sourcePage),
  };
}

export function markHasBooked(): void {
  safeLS()?.setItem(LS.hasBooked, "1");
}

export function hasBooked(): boolean {
  return safeLS()?.getItem(LS.hasBooked) === "1";
}

export function markDiscountDismissed(): void {
  const ls = safeLS();
  if (!ls) return;
  ls.setItem(LS.modalDismissedAt, String(Date.now()));
  const count = Number(ls.getItem(LS.modalDismissCount) || 0) + 1;
  ls.setItem(LS.modalDismissCount, String(count));
  // After the 2nd dismissal, skip the next 3 visits.
  if (count >= 2) {
    ls.setItem(LS.modalSkipUntilVisit, String(getVisitCount() + 3));
  }
}

const SS_DISMISSED = "gr_discount_modal_dismissed_session";
export function markDiscountDismissedThisSession(): void {
  safeSS()?.setItem(SS_DISMISSED, "1");
}
export function wasDismissedThisSession(): boolean {
  return safeSS()?.getItem(SS_DISMISSED) === "1";
}

export function shouldShowDiscountModal(): boolean {
  const ls = safeLS();
  if (!ls) return false;
  if (hasBooked()) return false;
  if (getVisitCount() < 2) return false;
  if (wasDismissedThisSession()) return false;
  const skipUntil = Number(ls.getItem(LS.modalSkipUntilVisit) || 0);
  if (skipUntil && getVisitCount() < skipUntil) return false;
  return true;
}

/** Initialize tracking on app boot. Safe to call multiple times. */
export function initVisitorTracking(): void {
  getVisitorId();
  getSessionId();
  getFirstTouch();
  incrementVisitIfNewSession();
}